import { useQuery } from '@tanstack/react-query';

import { getAuthTransport } from '@/lib/api/authTransport';
import { clearDelegationToken, delegationToken } from '@/lib/auth/delegationToken';
import { runtimeConfig } from '@/lib/config';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 에이전트가 **지금 할 수 있는 일** (agent `GET /api/v1/ai/capabilities`).
 *
 * 빈 대화 화면의 제안 칩이 이 목록으로 그려진다. 목록을 화면에 적지 않는 이유는 실제로 났던 일
 * 때문이다 — 화면은 "지금은 증명서 발급을 도와드릴 수 있습니다" 라고 적어 두었는데 그 사이 여덟
 * 도메인에 도구가 붙었다. **되는 일을 화면만 모르고 있었다.** 이제 도구를 붙이면 칩이 저절로 는다.
 *
 * `apiFetch` 를 쓰지 않는다 — 그쪽은 백엔드(:8080)의 `{ok,data}` 봉투를 벗기는 것이 일인데,
 * 여기는 에이전트(:8000)가 주는 평범한 JSON 이다. 신원은 대화와 같은 위임 토큰이 나른다.
 */

/** 도메인 하나. `examples` 는 라우터 few-shot 으로 검증된 **실제 발화**라 칩에 그대로 쓴다. */
export type AgentCapability = {
  key: string;
  label: string;
  examples: string[];
};

async function fetchCapabilities(): Promise<AgentCapability[]> {
  const transport = getAuthTransport();
  const token = await delegationToken();

  const response = await fetch(`${runtimeConfig().apiBaseUrl}/v1/ai/capabilities`, {
    // 조회라 unsafe: false — 쿠키 방식이 CSRF 헤더를 붙일지 판단하는 값이다.
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, ...transport.authHeaders({ unsafe: false }) },
    credentials: transport.credentials,
  });

  if (!response.ok) {
    // 토큰이 문제였을 수 있으니 캐시를 버린다 — 다음 시도(대화 전송 포함)는 새 토큰으로 나간다.
    if (response.status === 401) clearDelegationToken();
    throw new Error(`capabilities ${response.status}`);
  }

  const body = (await response.json()) as { domains?: AgentCapability[] };
  return body.domains ?? [];
}

/**
 * @param enabled 칩을 그릴 자리가 실제로 떠 있을 때만 켠다(2026-09-23 · DOC-94 §3 G).
 *        빈 대화에서만 쓰는 목록이라, 말이 쌓인 대화창이 열릴 때까지 요청을 내보낼 이유가 없다 —
 *        `useIssuanceTemplates(active)` 가 같은 이유로 같은 모양을 쓴다.
 */
export function useAgentCapabilities(enabled = true) {
  return useQuery({
    enabled,
    queryKey: queryKeys.aiAgentCapabilities,
    queryFn: fetchCapabilities,
    // 배포 전까지 바뀌지 않는 목록이라 한 번 받으면 그대로 쓴다.
    staleTime: 30 * 60 * 1000,
    // 실패해도 조용히 넘어간다 — 칩이 안 보일 뿐 대화는 그대로 된다. 에이전트가 떠 있지 않은
    // 개발 환경에서 재시도로 콘솔을 채울 이유가 없다.
    retry: false,
  });
}
